import { fetchSession, loginUrl, onUnauthorized, type Session, type Surface } from "@saas/api-client";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Alert, ButtonLink, Loading } from "./components";

/** The BFF only ever sends a fixed code; never echo query text into the page. */
const loginErrors: Record<string, string> = {
  access_denied: "Sign-in was cancelled or access was denied.",
  login_failed: "Sign-in failed. Please try again.",
};

export function loginErrorMessage(code: string | null): string | undefined {
  if (!code) return undefined;
  return loginErrors[code] ?? loginErrors["login_failed"];
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside <RequireRole>.");
  return session;
}

/**
 * Route guard for a whole surface: loads the BFF session, offers sign-in when there is none and refuses sessions
 * without the surface's role. The API enforces the same rules again on every call; this is only for UX.
 */
export function RequireRole({ surface, title, children }: { surface: Surface; title: string; children: ReactNode }) {
  const [state, setState] = useState<{ status: "loading" } | { status: "anonymous" } | { status: "error" } | { status: "ready"; session: Session }>({
    status: "loading",
  });

  useEffect(() => {
    document.documentElement.dataset["surface"] = surface;
    fetchSession(surface).then(
      (session) => setState(session ? { status: "ready", session } : { status: "anonymous" }),
      () => setState({ status: "error" }),
    );
  }, [surface]);

  useEffect(() => {
    // A 401 from any API call means the session ended (signed out elsewhere, revoked, expired): sign in again and
    // come back to the same page.
    onUnauthorized(() => window.location.assign(loginUrl(surface, window.location.pathname + window.location.search)));
    return () => onUnauthorized(undefined);
  }, [surface]);

  const loginError = loginErrorMessage(new URLSearchParams(window.location.search).get("error"));

  if (state.status === "loading") {
    return (
      <div className="sb-center">
        <Loading label="Checking your session…" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="sb-center">
        <Alert tone="danger">The sign-in service is unreachable. Please try again in a moment.</Alert>
      </div>
    );
  }

  if (state.status === "anonymous") {
    return (
      <div className="sb-center">
        <main className="sb-signin sb-card sb-card-body">
          <h1>{title}</h1>
          <p className="sb-muted">Sign in with your organisation account to continue.</p>
          {loginError && <Alert tone="danger">{loginError}</Alert>}
          <ButtonLink variant="primary" href={loginUrl(surface, window.location.pathname)}>
            Sign in
          </ButtonLink>
        </main>
      </div>
    );
  }

  if (!state.session.hasRequiredRole) {
    return (
      <div className="sb-center">
        <main className="sb-signin sb-card sb-card-body">
          <h1>No access</h1>
          <Alert tone="warning">
            You are signed in as {state.session.email}, but this area requires the {state.session.requiredRole} role.
          </Alert>
          <ButtonLink href={state.session.logoutUrl}>Sign out</ButtonLink>
        </main>
      </div>
    );
  }

  return <SessionContext.Provider value={state.session}>{children}</SessionContext.Provider>;
}

export function ErrorAlert({ error, live = true }: { error: Error | undefined; live?: boolean }) {
  return error ? (
    <Alert tone="danger" live={live}>
      {error.message}
    </Alert>
  ) : null;
}
